/**
 * #253: a document that does not exist is not a crash.
 *
 * `doc.data()` of a missing document is `undefined`, and every screen that
 * reads one goes straight on to destructure it — so a link to a month that was
 * deleted, an edited URL, or an employee removed while someone was looking at
 * them all ended at the generic "אופס! משהו השתבש", with the breadcrumb
 * reading "undefined undefined". On a payroll screen that looks like the data
 * is gone.
 *
 * The absence is a fact worth stating, not an error worth reporting, so it
 * travels as its own error type and the boundary renders it as an answer.
 */
export class DocumentNotFoundError extends Error {
  constructor(subject, path) {
    super(`${subject} לא נמצא`)
    this.name = 'DocumentNotFoundError'
    this.subject = subject
    this.path = path
  }
}

export const isDocumentNotFound = error => error instanceof DocumentNotFoundError ||
  error?.name === 'DocumentNotFoundError'

/**
 * True when a snapshot says its document is not there. Firestore answers with
 * `exists()`; the cached/enhanced wrappers only carry `data()`, and a missing
 * document reads as `undefined` through either. Anything that is neither (a
 * collection result, say) is not a missing document.
 */
export const isMissingDocument = snapshot => {
  if (!snapshot) {
    return true
  }
  if (typeof snapshot.exists === 'function') {
    return !snapshot.exists()
  }
  if (typeof snapshot.data === 'function') {
    return snapshot.data() === undefined
  }
  return false
}

/** Throws when the document is not there; returns the snapshot when it is. */
export function requireDocument(snapshot, subject, path) {
  if (isMissingDocument(snapshot)) {
    throw new DocumentNotFoundError(subject, path)
  }
  return snapshot
}
